export interface GameCategory {
  key: string;
  name: string;
  icon: string;
  desc: string;
}

export interface Game {
  id: string;
  name: string;
  cover: string;
  category: string;
  theme: string;
  tags: string[];
  releaseDate: string;
  developer: string;
  rating: number;
  players: number;
  summary: string;
  description: string;
  features: string[];
  screenshots: string[];
}

export interface Banner {
  gameId: string;
  title: string;
  subtitle: string;
  tag: string;
  image: string;
}

export const categories: GameCategory[] = [
  {
    key: "jingji",
    name: "竞技对战",
    icon: "ri-sword-line",
    desc: "实时匹配、团队开黑，比拼操作与临场策略。",
  },
  {
    key: "rpg",
    name: "角色扮演",
    icon: "ri-user-star-line",
    desc: "沉浸剧情与角色养成，打造属于你的冒险旅程。",
  },
  {
    key: "xiuxian",
    name: "休闲益智",
    icon: "ri-emotion-happy-line",
    desc: "轻松上手、随时开局，碎片时间也能玩上几把。",
  },
  {
    key: "celue",
    name: "策略塔防",
    icon: "ri-layout-grid-line",
    desc: "排兵布阵、运筹帷幄，用头脑赢下每一场战争。",
  },
  {
    key: "sheji",
    name: "射击冒险",
    icon: "ri-crosshair-line",
    desc: "枪林弹雨与未知探险，考验反应与胆量。",
  },
  {
    key: "tiyu",
    name: "体育竞速",
    icon: "ri-football-line",
    desc: "极速飞驰、挥洒汗水，感受竞技体育的激情。",
  },
];

export const themeOptions: string[] = ["奇幻", "科幻", "武侠", "历史", "现代", "冒险"];

export const tagOptions: string[] = [
  "单人",
  "多人",
  "团队",
  "5v5",
  "回合制",
  "放置",
  "闯关",
  "竞技",
  "养成",
  "高难度",
  "急速",
  "轻松",
];

export const banners: Banner[] = [
  {
    gameId: "huanjing-duijue",
    title: "幻境对决",
    subtitle: "5v5 奇幻竞技，集结队友开启一场巅峰对决",
    tag: "本周主打",
    image:
      "https://readdy.ai/api/search-image?query=epic%20fantasy%20multiplayer%20arena%20battle%20between%20glowing%20magical%20heroes%2C%20wide%20cinematic%20key%20art%2C%20clean%20dramatic%20gradient%20background%2C%20rich%20colors%2C%20highly%20detailed%20digital%20illustration&width=1600&height=800&seq=gwj-banner-01&orientation=landscape",
  },
  {
    gameId: "jianying-jianghu",
    title: "剑影江湖",
    subtitle: "走进水墨武侠世界，行侠仗义快意恩仇",
    tag: "人气飙升",
    image:
      "https://readdy.ai/api/search-image?query=wandering%20swordsman%20in%20flowing%20robes%20in%20an%20ancient%20bamboo%20forest%20at%20dusk%2C%20wide%20cinematic%20key%20art%2C%20clean%20dramatic%20gradient%20background%2C%20rich%20colors%2C%20highly%20detailed%20digital%20illustration&width=1600&height=800&seq=gwj-banner-02&orientation=landscape",
  },
  {
    gameId: "anye-juji",
    title: "暗夜狙击",
    subtitle: "雨夜潜行，一枪定胜负的硬核射击体验",
    tag: "编辑推荐",
    image:
      "https://readdy.ai/api/search-image?query=lone%20sniper%20in%20tactical%20gear%20aiming%20a%20rifle%20through%20a%20rainy%20neon%20night%20city%2C%20wide%20cinematic%20key%20art%2C%20clean%20dramatic%20gradient%20background%2C%20rich%20colors%2C%20highly%20detailed%20digital%20illustration&width=1600&height=800&seq=gwj-banner-03&orientation=landscape",
  },
  {
    gameId: "jixian-piaoyi",
    title: "极限漂移",
    subtitle: "山路弯道全速过弯，漂移就是最好的语言",
    tag: "热门竞速",
    image:
      "https://readdy.ai/api/search-image?query=sport%20car%20drifting%20with%20thick%20tire%20smoke%20on%20a%20mountain%20road%20at%20sunset%2C%20wide%20cinematic%20key%20art%2C%20clean%20dramatic%20gradient%20background%2C%20rich%20colors%2C%20highly%20detailed%20digital%20illustration&width=1600&height=800&seq=gwj-banner-04&orientation=landscape",
  },
];

export const games: Game[] = [
  {
    id: "huanjing-duijue",
    name: "幻境对决",
    cover:
      "https://readdy.ai/api/search-image?query=epic%20fantasy%20multiplayer%20arena%20battle%20between%20glowing%20magical%20heroes%20with%20runic%20energy%20effects%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-01&orientation=portrait",
    category: "jingji",
    theme: "奇幻",
    tags: ["5v5", "团队", "竞技"],
    releaseDate: "2026-09-18",
    developer: "星火互娱",
    rating: 9.2,
    players: 4820000,
    summary: "5v5 奇幻竞技对战，选择你的英雄，在幻境战场上一决高下。",
    description:
      "幻境对决是一款快节奏的 5v5 竞技对战游戏，玩家将扮演拥有独特技能的奇幻英雄，在三条路线的战场上与队友协同推进。游戏拥有丰富的地图机制与英雄组合，可自由搭配出装与天赋。每局约 15 分钟，适合随时开黑的竞技玩家，操作与策略并重。",
    features: ["超过 60 位风格各异的英雄", "实时匹配，快速开局", "天赋与出装自由搭配", "赛季排位与段位晋升"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=epic%20fantasy%20multiplayer%20arena%20battle%20with%20glowing%20magic%20effects%2C%20wide%20gameplay%20scene%2C%20dynamic%20action%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-01-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=fantasy%20heroes%20casting%20spells%20on%20a%20battlefield%20map%2C%20wide%20gameplay%20scene%2C%20dramatic%20lighting%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-01-s2&orientation=landscape",
    ],
  },
  {
    id: "jisu-zhanche",
    name: "极速战车",
    cover:
      "https://readdy.ai/api/search-image?query=futuristic%20armored%20racing%20cars%20speeding%20on%20a%20neon%20highway%20with%20motion%20blur%20and%20flying%20sparks%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-02&orientation=portrait",
    category: "jingji",
    theme: "科幻",
    tags: ["多人", "竞技", "急速"],
    releaseDate: "2026-08-02",
    developer: "火线工作室",
    rating: 8.7,
    players: 2130000,
    summary: "武装改装 + 高速竞速，用速度与火力冲过终点线。",
    description:
      "极速战车把赛车竞速和武装对战结合在一起，玩家可以自由改装引擎、轮胎与车载武器，在充满机关与捷径的赛道上与对手一较高下。既能拼驾驶技术，也能用火力压制对手，节奏紧张刺激。",
    features: ["自由改装车辆部件", "8 人实时竞速对战", "机关赛道与捷径设计", "多种比赛模式切换"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=futuristic%20racing%20cars%20with%20weapons%20speeding%20on%20a%20neon%20track%2C%20wide%20gameplay%20scene%2C%20motion%20blur%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-02-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=armored%20vehicles%20racing%20through%20a%20futuristic%20cyber%20city%20at%20night%2C%20wide%20gameplay%20scene%2C%20glowing%20lights%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-02-s2&orientation=landscape",
    ],
  },
  {
    id: "quanwang-zhengba",
    name: "拳王争霸",
    cover:
      "https://readdy.ai/api/search-image?query=two%20powerful%20martial%20artists%20facing%20off%20in%20a%20bright%20boxing%20ring%20under%20spotlights%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-03&orientation=portrait",
    category: "jingji",
    theme: "现代",
    tags: ["竞技", "单人", "高难度"],
    releaseDate: "2026-06-21",
    developer: "雷霆竞技",
    rating: 8.4,
    players: 1560000,
    summary: "硬核格斗竞技，搓招连击与心理博弈的正面碰撞。",
    description:
      "拳王争霸是一款注重操作细节的格斗竞技游戏，拥有数十位风格不同的格斗家。玩家需要熟悉连招、躲避与格挡时机，在攻防转换中寻找破绽。既有适合新手的教学关卡，也有面向高手的排位赛。",
    features: ["搓招连击操作系统", "数十位格斗家可选", "连招训练与教学", "在线排位对战"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=martial%20artists%20exchanging%20powerful%20punches%20in%20a%20bright%20arena%2C%20wide%20gameplay%20scene%2C%20dynamic%20impact%20effects%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-03-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=fighter%20performing%20a%20high%20kick%20under%20stadium%20spotlights%2C%20wide%20gameplay%20scene%2C%20dramatic%20lighting%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-03-s2&orientation=landscape",
    ],
  },
  {
    id: "jianying-jianghu",
    name: "剑影江湖",
    cover:
      "https://readdy.ai/api/search-image?query=wandering%20swordsman%20in%20flowing%20robes%20standing%20in%20an%20ancient%20bamboo%20forest%20at%20dusk%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-04&orientation=portrait",
    category: "rpg",
    theme: "武侠",
    tags: ["单人", "养成", "闯关"],
    releaseDate: "2026-09-06",
    developer: "墨白游戏",
    rating: 9.0,
    players: 3270000,
    summary: "水墨武侠角色扮演，行走江湖、习武成长、快意恩仇。",
    description:
      "剑影江湖以水墨画风呈现一个开放的武侠世界。玩家从无名小卒起步，通过拜师学艺、闯荡江湖逐步成长为一代大侠。游戏包含丰富的支线剧情、武学养成与门派选择，每一次抉择都会影响故事走向。",
    features: ["水墨画风开放武侠世界", "门派与武学自由养成", "多线剧情与结局", "实时战斗与轻功系统"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=swordsman%20using%20a%20flowing%20sword%20technique%20in%20a%20misty%20bamboo%20forest%2C%20wide%20gameplay%20scene%2C%20ink%20painting%20style%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-04-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=ancient%20chinese%20jianghu%20town%20at%20sunset%20with%20a%20wandering%20hero%2C%20wide%20gameplay%20scene%2C%20ink%20painting%20style%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-04-s2&orientation=landscape",
    ],
  },
  {
    id: "longzhi-jiyuan",
    name: "龙之纪元",
    cover:
      "https://readdy.ai/api/search-image?query=young%20dragon%20knight%20riding%20a%20mighty%20dragon%20above%20a%20fantasy%20kingdom%20of%20towers%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-05&orientation=portrait",
    category: "rpg",
    theme: "奇幻",
    tags: ["单人", "养成", "回合制"],
    releaseDate: "2026-07-12",
    developer: "星火互娱",
    rating: 8.8,
    players: 2760000,
    summary: "驯龙冒险角色扮演，回合制战斗搭配深度养成。",
    description:
      "龙之纪元讲述少年与幼龙共同成长、拯救王国的故事。玩家可以在广阔的大陆上探索副本、收集龙蛋，并通过回合制战斗培养龙与伙伴。技能搭配与站位策略是战斗胜负的关键，适合喜欢慢节奏养成的玩家。",
    features: ["龙的收集与进化养成", "策略回合制战斗", "大陆探索与副本解谜", "伙伴羁绊与专属剧情"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=dragon%20knight%20flying%20over%20a%20fantasy%20castle%20with%20glowing%20sky%2C%20wide%20gameplay%20scene%2C%20epic%20scale%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-05-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=team%20of%20adventurers%20battling%20a%20giant%20dragon%20in%20a%20ruined%20temple%2C%20wide%20gameplay%20scene%2C%20epic%20scale%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-05-s2&orientation=landscape",
    ],
  },
  {
    id: "xinghai-lvren",
    name: "星海旅人",
    cover:
      "https://readdy.ai/api/search-image?query=lone%20space%20explorer%20in%20sleek%20armor%20gazing%20at%20a%20glowing%20alien%20planet%20and%20stars%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-06&orientation=portrait",
    category: "rpg",
    theme: "科幻",
    tags: ["单人", "闯关", "养成"],
    releaseDate: "2026-05-29",
    developer: "深空工作室",
    rating: 8.6,
    players: 1420000,
    summary: "星际探险角色扮演，驾驶飞船探索未知星球。",
    description:
      "星海旅人将角色扮演与太空探索结合，玩家扮演一名星际旅人，驾驶飞船穿梭于各个星系，降落未知星球采集资源、结识外星文明。飞船与角色均可升级，故事围绕一段失落文明的谜团展开。",
    features: ["星系自由航行探索", "飞船与角色升级系统", "外星文明剧情抉择", "资源采集与制作"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=space%20explorer%20landing%20on%20a%20glowing%20alien%20planet%20with%20colorful%20sky%2C%20wide%20gameplay%20scene%2C%20sci-fi%20atmosphere%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-06-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=spaceship%20flying%20through%20a%20nebula%20toward%20a%20ringed%20planet%2C%20wide%20gameplay%20scene%2C%20sci-fi%20atmosphere%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-06-s2&orientation=landscape",
    ],
  },
  {
    id: "shuiguo-xiaoxiaole",
    name: "水果消消乐",
    cover:
      "https://readdy.ai/api/search-image?query=colorful%20cheerful%20cartoon%20fruits%20with%20happy%20faces%20and%20sparkling%20magic%20dust%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-07&orientation=portrait",
    category: "xiuxian",
    theme: "现代",
    tags: ["单人", "轻松", "闯关"],
    releaseDate: "2026-09-27",
    developer: "甜橙娱乐",
    rating: 8.9,
    players: 3940000,
    summary: "老少皆宜的三消游戏，轻松连消解锁趣味关卡。",
    description:
      "水果消消乐是一款轻松休闲的三消游戏，通过交换相邻水果凑齐三个以上即可消除。游戏包含数百个精心设计的关卡，搭配道具与每日挑战，玩法简单但富有策略。适合碎片时间来上几局，放松心情。",
    features: ["数百个趣味关卡", "道具与连击加成", "每日挑战与签到", "一键上手随时开局"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=colorful%20cartoon%20fruit%20matching%20puzzle%20board%20with%20sparkle%20effects%2C%20wide%20gameplay%20scene%2C%20bright%20cheerful%20colors%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-07-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=happy%20cartoon%20fruits%20celebrating%20a%20level%20win%20with%20confetti%2C%20wide%20gameplay%20scene%2C%20bright%20cheerful%20colors%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-07-s2&orientation=landscape",
    ],
  },
  {
    id: "paopao-dazuozhan",
    name: "泡泡大作战",
    cover:
      "https://readdy.ai/api/search-image?query=cute%20round%20bubble%20characters%20bursting%20with%20rainbow%20colors%20and%20playful%20soap%20bubbles%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-08&orientation=portrait",
    category: "xiuxian",
    theme: "奇幻",
    tags: ["多人", "轻松", "竞技"],
    releaseDate: "2026-04-15",
    developer: "泡泡工坊",
    rating: 8.3,
    players: 1870000,
    summary: "可爱泡泡对战，放泡泡、拆泡泡，欢乐多人休闲。",
    description:
      "泡泡大作战是一款轻松的多人休闲对战游戏，玩家操控可爱的泡泡角色，在场地中布置泡泡陷阱并炸开对手。操作简单，一局只需几分钟，非常适合和好友一起开黑。多种地图与可爱的皮肤让游戏充满乐趣。",
    features: ["欢乐多人对战", "多种主题地图", "可爱角色皮肤", "快速匹配开局"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=cute%20bubble%20characters%20playing%20in%20a%20colorful%20arena%20with%20soap%20bubbles%2C%20wide%20gameplay%20scene%2C%20playful%20and%20bright%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-08-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=rainbow%20bubbles%20exploding%20on%20a%20cheerful%20playground%20map%2C%20wide%20gameplay%20scene%2C%20playful%20and%20bright%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-08-s2&orientation=landscape",
    ],
  },
  {
    id: "micheng-fangkuai",
    name: "迷城方块",
    cover:
      "https://readdy.ai/api/search-image?query=glowing%20cubes%20and%20puzzle%20blocks%20forming%20a%20maze%20city%20with%20luminous%20paths%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-09&orientation=portrait",
    category: "xiuxian",
    theme: "科幻",
    tags: ["单人", "高难度", "闯关"],
    releaseDate: "2026-08-24",
    developer: "方寸工作室",
    rating: 8.5,
    players: 980000,
    summary: "烧脑方块解谜，旋转推拉之间找到唯一出路。",
    description:
      "迷城方块是一款以方块空间为核心的解谜游戏，玩家需要移动、旋转和堆叠方块，为角色搭出一条通往终点的道路。关卡由浅入深，后期考验空间想象力与耐心，适合喜欢动脑的解谜玩家。",
    features: ["精心设计的高难度关卡", "立体空间解谜机制", "最少步数挑战", "关卡编辑器与分享"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=glowing%20puzzle%20cubes%20arranged%20in%20a%20floating%20maze%20structure%2C%20wide%20gameplay%20scene%2C%20cool%20sci-fi%20lighting%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-09-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=a%20character%20standing%20on%20glowing%20blocks%20building%20a%20path%2C%20wide%20gameplay%20scene%2C%20cool%20sci-fi%20lighting%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-09-s2&orientation=landscape",
    ],
  },
  {
    id: "wangguo-shouweizhan",
    name: "王国守卫战",
    cover:
      "https://readdy.ai/api/search-image?query=fantasy%20castle%20towers%20defending%20against%20a%20monster%20army%20with%20knights%20and%20banners%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-10&orientation=portrait",
    category: "celue",
    theme: "奇幻",
    tags: ["单人", "回合制", "闯关"],
    releaseDate: "2026-07-30",
    developer: "铁壁工作室",
    rating: 8.8,
    players: 2210000,
    summary: "经典塔防策略，布置防御塔抵挡一波波怪物进攻。",
    description:
      "王国守卫战是一款经典塔防策略游戏，玩家需要在道路两侧建造不同类型的防御塔，阻挡怪物抵达城堡。合理搭配弓箭塔、魔法塔与陷阱，并根据敌人类型调整布局，是通关的关键。随着关卡推进，还将解锁英雄单位。",
    features: ["多样防御塔自由搭配", "英雄单位与技能", "数十张地图关卡", "无尽模式挑战排行"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=fantasy%20kingdom%20defense%20towers%20firing%20at%20invading%20monsters%20on%20a%20map%2C%20wide%20gameplay%20scene%2C%20epic%20scale%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-10-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=hero%20defending%20a%20castle%20gate%20against%20a%20monster%20horde%20at%20dusk%2C%20wide%20gameplay%20scene%2C%20epic%20scale%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-10-s2&orientation=landscape",
    ],
  },
  {
    id: "diguo-baye",
    name: "帝国霸业",
    cover:
      "https://readdy.ai/api/search-image?query=grand%20empire%20army%20marching%20with%20golden%20banners%20across%20vast%20sunlit%20plains%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-11&orientation=portrait",
    category: "celue",
    theme: "历史",
    tags: ["多人", "回合制", "竞技"],
    releaseDate: "2026-06-08",
    developer: "长风互娱",
    rating: 8.2,
    players: 1650000,
    summary: "文明经营与军事对抗，从城邦起步建立你的帝国。",
    description:
      "帝国霸业是一款历史题材的策略游戏，玩家从一座小城邦开始，发展农业、贸易与科技，组建军队并与其他势力周旋。外交、结盟与战争同样重要，最终目标是统一大陆，建立属于自己的帝国。",
    features: ["多文明发展路线", "外交结盟与战争", "科技与经济经营", "多人联盟对战"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=ancient%20empire%20armies%20clashing%20on%20a%20sunlit%20battlefield%20with%20banners%2C%20wide%20gameplay%20scene%2C%20epic%20scale%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-11-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=ancient%20city%20overview%20map%20with%20farmland%20and%20walls%2C%20wide%20gameplay%20scene%2C%20epic%20scale%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-11-s2&orientation=landscape",
    ],
  },
  {
    id: "sanguo-fenghuo",
    name: "三国烽火",
    cover:
      "https://readdy.ai/api/search-image?query=ancient%20chinese%20warriors%20on%20horseback%20charging%20into%20battle%20with%20red%20banners%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-12&orientation=portrait",
    category: "celue",
    theme: "历史",
    tags: ["单人", "回合制", "养成"],
    releaseDate: "2026-03-19",
    developer: "虎啸工作室",
    rating: 8.7,
    players: 2480000,
    summary: "三国题材策略养成，招兵买马席卷天下群雄。",
    description:
      "三国烽火以群雄逐鹿的历史为背景，玩家扮演一方诸侯，招募名将、发展内政、征战四方。武将之间的兵种克制与阵型搭配决定战局走向，配合真实的势力对立与随机事件，带来不同的统一之路。",
    features: ["众多三国名将招募养成", "兵种克制与阵型搭配", "内政发展与征战扩张", "随机事件与多结局"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=three%20kingdoms%20cavalry%20charging%20under%20red%20banners%20on%20a%20dusty%20battlefield%2C%20wide%20gameplay%20scene%2C%20epic%20scale%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-12-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=ancient%20chinese%20war%20council%20tent%20with%20maps%20and%20generals%2C%20wide%20gameplay%20scene%2C%20epic%20scale%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-12-s2&orientation=landscape",
    ],
  },
  {
    id: "anye-juji",
    name: "暗夜狙击",
    cover:
      "https://readdy.ai/api/search-image?query=lone%20sniper%20in%20tactical%20gear%20aiming%20a%20rifle%20through%20a%20rainy%20night%20city%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-13&orientation=portrait",
    category: "sheji",
    theme: "现代",
    tags: ["单人", "高难度", "闯关"],
    releaseDate: "2026-09-12",
    developer: "夜枭工作室",
    rating: 9.1,
    players: 3560000,
    summary: "硬核狙击射击，潜行与一击致命并存的夜色战场。",
    description:
      "暗夜狙击是一款强调潜行与精准的射击游戏，玩家扮演一名特工狙击手，在雨夜城市中完成任务。需要观察风向、计算弹道，选择合适的时机与位置。被发现后也会进入紧张的战斗，考验反应与战术判断。",
    features: ["真实弹道与风力系统", "潜行与暗杀玩法", "剧情关卡与挑战模式", "多种狙击枪改装"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=sniper%20aiming%20through%20a%20scope%20at%20a%20rainy%20neon%20city%20rooftop%2C%20wide%20gameplay%20scene%2C%20tense%20atmosphere%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-13-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=tactical%20agent%20moving%20through%20a%20dark%20rainy%20city%20street%20at%20night%2C%20wide%20gameplay%20scene%2C%20tense%20atmosphere%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-13-s2&orientation=landscape",
    ],
  },
  {
    id: "xingji-yuanzheng",
    name: "星际远征",
    cover:
      "https://readdy.ai/api/search-image?query=space%20marines%20in%20futuristic%20armor%20fighting%20aliens%20inside%20a%20glowing%20spaceship%20corridor%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-14&orientation=portrait",
    category: "sheji",
    theme: "科幻",
    tags: ["多人", "竞技", "团队"],
    releaseDate: "2026-08-16",
    developer: "深空工作室",
    rating: 8.6,
    players: 1780000,
    summary: "科幻多人射击，与队友守住飞船击退外星生物。",
    description:
      "星际远征是一款科幻题材的多人射击游戏，玩家组成小队在飞船与空间站中对抗外星生物。不同职业拥有独特武器与技能，需要合理分工合作才能守住据点。紧凑的战斗节奏与多样的地图让每局都充满变数。",
    features: ["多人合作射击", "多职业与武器搭配", "科幻地图与BOSS战", "团队语音与配合"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=space%20marines%20firing%20blasters%20at%20aliens%20in%20a%20glowing%20spaceship%20hallway%2C%20wide%20gameplay%20scene%2C%20sci-fi%20action%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-14-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=tactical%20squad%20defending%20a%20control%20room%20from%20alien%20attack%2C%20wide%20gameplay%20scene%2C%20sci-fi%20action%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-14-s2&orientation=landscape",
    ],
  },
  {
    id: "huangye-qiuzheng",
    name: "荒野求生记",
    cover:
      "https://readdy.ai/api/search-image?query=adventurer%20with%20a%20backpack%20exploring%20a%20wild%20jungle%20canyon%20with%20waterfalls%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-15&orientation=portrait",
    category: "sheji",
    theme: "冒险",
    tags: ["单人", "闯关", "养成"],
    releaseDate: "2026-05-05",
    developer: "野径工作室",
    rating: 8.4,
    players: 1240000,
    summary: "荒野冒险生存，一边探索一边与野兽周旋。",
    description:
      "荒野求生记将冒险探索与生存射击结合，玩家在广袤的荒野中采集资源、搭建营地，并与野兽和各种危险对抗。地图上散布着待解开的谜题与遗迹，越深入，挑战越强，适合喜欢探索的玩家。",
    features: ["开放荒野地图探索", "生存采集与制作", "与野兽战斗", "遗迹谜题与宝藏"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=adventurer%20climbing%20a%20rocky%20canyon%20with%20glowing%20waterfalls%2C%20wide%20gameplay%20scene%2C%20lush%20nature%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-15-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=campsite%20in%20a%20wild%20forest%20at%20night%20with%20a%20campfire%2C%20wide%20gameplay%20scene%2C%20lush%20nature%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-15-s2&orientation=landscape",
    ],
  },
  {
    id: "jixian-piaoyi",
    name: "极限漂移",
    cover:
      "https://readdy.ai/api/search-image?query=sport%20car%20drifting%20with%20thick%20tire%20smoke%20on%20a%20mountain%20road%20at%20sunset%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-16&orientation=portrait",
    category: "tiyu",
    theme: "现代",
    tags: ["多人", "竞技", "急速"],
    releaseDate: "2026-09-21",
    developer: "火线工作室",
    rating: 8.9,
    players: 2650000,
    summary: "漂移竞速模拟，掌握油门与手刹成为弯道之王。",
    description:
      "极限漂移是一款注重驾驶手感与技巧的竞速游戏，玩家可以在山路、赛道与城市街道上挑战漂移。通过合理的油门、方向与手刹配合，冲刺更高的漂移分数。支持多人对战，也能单人刷分挑战自我。",
    features: ["真实漂移手感", "多种赛道与车辆", "多人竞速对战", "漂移分数排行榜"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=sport%20car%20drifting%20around%20a%20mountain%20hairpin%20with%20smoke%20at%20sunset%2C%20wide%20gameplay%20scene%2C%20dynamic%20speed%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-16-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=race%20cars%20speeding%20side%20by%20side%20on%20a%20coastal%20highway%2C%20wide%20gameplay%20scene%2C%20dynamic%20speed%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-16-s2&orientation=landscape",
    ],
  },
  {
    id: "jietou-lanqiuwang",
    name: "街头篮球王",
    cover:
      "https://readdy.ai/api/search-image?query=energetic%20street%20basketball%20player%20dunking%20on%20an%20outdoor%20urban%20court%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-17&orientation=portrait",
    category: "tiyu",
    theme: "现代",
    tags: ["多人", "竞技", "团队"],
    releaseDate: "2026-07-03",
    developer: "街头体育",
    rating: 8.5,
    players: 1930000,
    summary: "3v3 街头篮球，花式操作与团队配合一起上分。",
    description:
      "街头篮球王是一款 3v3 的街头篮球竞技游戏，玩家可以选择不同位置的球员，通过运球、突破与投篮得分。花式操作带来爽快的视觉表现，而团队配合与战术意识才是取胜的关键。",
    features: ["3v3 实时对战", "多位置球员养成", "花式操作与技能", "排位赛与好友组队"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=street%20basketball%20players%20doing%20a%20flashy%20dunk%20on%20an%20urban%20court%2C%20wide%20gameplay%20scene%2C%20energetic%20action%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-17-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=3v3%20basketball%20match%20in%20a%20colorful%20street%20court%20with%20crowd%2C%20wide%20gameplay%20scene%2C%20energetic%20action%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-17-s2&orientation=landscape",
    ],
  },
  {
    id: "lvyin-chuanqi",
    name: "绿茵传奇",
    cover:
      "https://readdy.ai/api/search-image?query=soccer%20player%20striking%20the%20ball%20in%20a%20packed%20stadium%20with%20bright%20floodlights%2C%20vibrant%20stylized%20game%20key%20art%2C%20clean%20simple%20gradient%20background%2C%20centered%20composition%2C%20dramatic%20rim%20light%2C%20highly%20detailed%20digital%20illustration&width=600&height=800&seq=gwj-cover-18&orientation=portrait",
    category: "tiyu",
    theme: "现代",
    tags: ["多人", "竞技", "团队"],
    releaseDate: "2026-02-26",
    developer: "绿茵体育",
    rating: 8.6,
    players: 2240000,
    summary: "足球竞技经营，从管理球队到亲临赛场角逐冠军。",
    description:
      "绿茵传奇是一款足球竞技游戏，玩家既能以球员身份在赛场上奔跑、传球与射门，也能以经理身份经营球队、调整阵容与战术。真实的比赛节奏与丰富的战术选择，让每一场比赛都充满变数。",
    features: ["球员与经理双模式", "真实比赛操作手感", "球队经营与阵容搭配", "联赛杯赛与多人对战"],
    screenshots: [
      "https://readdy.ai/api/search-image?query=soccer%20player%20scoring%20a%20goal%20in%20a%20packed%20stadium%20at%20night%2C%20wide%20gameplay%20scene%2C%20energetic%20action%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-18-s1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=football%20match%20overview%20of%20a%20green%20pitch%20full%20of%20players%20under%20floodlights%2C%20wide%20gameplay%20scene%2C%20energetic%20action%2C%20vibrant%20stylized%20game%20art%2C%20clean%20colorful%20background&width=1200&height=675&seq=gwj-18-s2&orientation=landscape",
    ],
  },
];